import React, { useEffect, useRef, useState } from 'react';
import type { VitalFormData } from '../../types';
import { PAIN_SCALES } from '../../../../../dtos/patientmr/charting/vitals.dto';
import { FieldsProps, InputField, SelectField } from './fieldParts';
import { UNUSUAL_NOTE, unusualVitals } from '../../model/entryValidation';

const PAIN_SCALE_LABELS: Record<string, string> = { FLACC: 'FLACC', FACES: 'FACES', NRS: 'NRS (0–10)' };

export type TempUnit = 'F' | 'C';
const UNIT_KEY = 'pelora.chart.tempUnit';

/** °C → stored °F text with one decimal ('' for blank or not a number) */
export const cToF = (c: string): string => {
  if (c.trim() === '' || !Number.isFinite(Number(c))) return '';
  return (Number(c) * 9 / 5 + 32).toFixed(1);
};
/** Stored °F text → °C text with one decimal */
export const fToC = (f: string): string => {
  if (f.trim() === '' || !Number.isFinite(Number(f))) return '';
  return ((Number(f) - 32) * 5 / 9).toFixed(1);
};

/** "110/70" → ['110', '70']; the BP stays one `bloodPressure` string in the form and payload */
export const splitBp = (bp: string): [string, string] => {
  const [s = '', d = ''] = String(bp || '').split('/');
  return [s, d];
};
export const joinBp = (systolic: string, diastolic: string): string =>
  (systolic === '' && diastolic === '' ? '' : `${systolic}/${diastolic}`);

const readUnit = (): TempUnit => {
  try {
    return window.localStorage.getItem(UNIT_KEY) === 'C' ? 'C' : 'F';
  } catch {
    return 'F';
  }
};
const writeUnit = (u: TempUnit) => {
  try {
    window.localStorage.setItem(UNIT_KEY, u);
  } catch {
    // a per-device convenience only
  }
};

/**
 * Vitals (Phase 18 M1 bounds; M8 pain scale/score and O2 flow). Phase 18U U8: BP as two numeric
 * fields (the cursor moves to diastolic after three digits), numeric keypads, a °F / °C toggle
 * that always stores °F, and an amber "unusual value" note that never blocks saving.
 */
const VitalsFields: React.FC<FieldsProps<VitalFormData> & { firstRef?: React.Ref<HTMLInputElement> }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, firstRef,
}) => {
  const set = (field: keyof VitalFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-blue-700' };
  const [unit, setUnit] = useState<TempUnit>(readUnit);
  // What the nurse typed in °C (kept as typed; the stored value is °F)
  const [celsius, setCelsius] = useState<string>(() => fToC(value.temperature));
  const diastolicRef = useRef<HTMLInputElement>(null);
  const [systolic, diastolic] = splitBp(value.bloodPressure);
  const unusual = unusualVitals(value);
  const hint = (field: string) => (unusual.includes(field) ? UNUSUAL_NOTE : undefined);

  // Copy last / resume replace the stored °F: show it in °C again
  useEffect(() => {
    if (unit === 'C' && cToF(celsius) !== value.temperature) setCelsius(fToC(value.temperature));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.temperature, unit]);

  const switchUnit = (u: TempUnit) => {
    setUnit(u);
    writeUnit(u);
    if (u === 'C') setCelsius(fToC(value.temperature));
  };

  const onSystolic = (s: string) => {
    onChange({ ...value, bloodPressure: joinBp(s, diastolic) });
    if (s.length >= 3) diastolicRef.current?.focus();
  };

  return (
    <div className={`grid gap-1.5 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
      <div className="space-y-0.5">
        <InputField {...common} id="vitals-temperature" label={unit === 'C' ? 'Temp °C' : 'Temp °F'} type="number" inputMode="decimal"
          placeholder={unit === 'C' ? '37.0' : '98.6'} inputRef={firstRef}
          value={unit === 'C' ? celsius : value.temperature}
          onChange={unit === 'C' ? (c) => { setCelsius(c); onChange({ ...value, temperature: cToF(c) }); } : set('temperature')}
          error={errors.temperature} hint={hint('temperature')} />
        <div className="flex gap-1" role="radiogroup" aria-label="Temperature unit">
          {(['F', 'C'] as const).map((u) => (
            <button key={u} type="button" role="radio" aria-checked={unit === u} onClick={() => switchUnit(u)} disabled={disabled}
              className={`rounded border px-2 text-xs font-semibold ${isMobile ? 'min-h-[44px] min-w-[44px]' : 'py-0.5'} ${unit === u ? 'border-blue-700 bg-blue-700 text-white' : 'border-gray-300 bg-white text-gray-700'}`}>
              °{u}
            </button>
          ))}
        </div>
      </div>
      <fieldset className="col-span-1 space-y-0.5" aria-describedby={errors.bloodPressure ? 'vitals-bp-dia-error' : undefined}>
        <legend className="text-xs font-medium text-blue-700">BP mmHg</legend>
        <div className="flex items-start gap-1">
          <InputField {...common} id="vitals-bp-sys" label="" ariaLabel="Systolic" type="text" inputMode="numeric" placeholder="110"
            value={systolic} onChange={(s) => onSystolic(s.replace(/\D/g, '').slice(0, 3))} className="flex-1" />
          <span className="pt-2 text-gray-500" aria-hidden="true">/</span>
          <InputField {...common} id="vitals-bp-dia" label="" ariaLabel="Diastolic" type="text" inputMode="numeric" placeholder="70" inputRef={diastolicRef}
            value={diastolic} onChange={(d) => onChange({ ...value, bloodPressure: joinBp(systolic, d.replace(/\D/g, '').slice(0, 3)) })} className="flex-1"
            error={errors.bloodPressure} hint={hint('bloodPressure')} />
        </div>
      </fieldset>
      <InputField {...common} id="vitals-pulse" label="Pulse BPM" type="number" inputMode="numeric" placeholder="96"
        value={value.pulse} onChange={set('pulse')} error={errors.pulse} hint={hint('pulse')} />
      <InputField {...common} id="vitals-rr" label="RR /min" type="number" inputMode="numeric" placeholder="24"
        value={value.respiratoryRate} onChange={set('respiratoryRate')} error={errors.respiratoryRate} hint={hint('respiratoryRate')} />
      <InputField {...common} id="vitals-spo2" label="O2 Sat %" type="number" inputMode="numeric" placeholder="98"
        value={value.o2Saturation} onChange={set('o2Saturation')} error={errors.o2Saturation} hint={hint('o2Saturation')} />
      <InputField {...common} id="vitals-o2flow" label="O2 flow L/min" type="number" placeholder="0.5"
        value={value.o2FlowLpm} onChange={set('o2FlowLpm')} error={errors.o2FlowLpm} />
      <SelectField {...common} id="vitals-pain-scale" label="Pain scale"
        options={PAIN_SCALES.map((s) => ({ value: s, label: PAIN_SCALE_LABELS[s] || s }))}
        value={value.painScale} onChange={set('painScale')} error={errors['pain.scale']} />
      <InputField {...common} id="vitals-pain-score" label="Pain score" type="number" inputMode="numeric" placeholder="0–10"
        value={value.painScore} onChange={set('painScore')} error={errors['pain.score']} />
    </div>
  );
};

export default VitalsFields;
